export const ORDER_TRANSITIONS = {
    DRAFT: [
        { to: 'PENDING_PAYMENT', allowedRoles: ['customer', 'system'], label: 'Proceed to payment' },
        { to: 'CANCELLED', allowedRoles: ['customer', 'admin'], label: 'Cancel' },
    ],
    PENDING_PAYMENT: [
        { to: 'PAID', allowedRoles: ['system'], label: 'Payment captured' },
        { to: 'CANCELLED', allowedRoles: ['customer', 'admin', 'system'], label: 'Cancel' },
    ],
    PAID: [
        { to: 'ARTWORK_REVIEW', allowedRoles: ['system', 'admin'], label: 'Send to prepress' },
        { to: 'REFUNDED', allowedRoles: ['admin'], label: 'Refund' },
    ],
    ARTWORK_REVIEW: [
        /*
         * SYSTEM ONLY, so the admin console cannot offer this as a button.
         *
         * There were two routes to ASSIGNED and only one of them did the work.
         * `assignVendor` creates the vendorJob and moves the order in one
         * operation; this entry put an "Assign to vendor" button in the status
         * panel that wrote the status and nothing else. An order would then read
         * ASSIGNED -> VENDOR_ACCEPTED -> IN_PRODUCTION with no job in existence,
         * so the vendor's portal was empty while the order said they were printing
         * it. That happened on EPS/26-27/0004 on 3 Aug 2026.
         *
         * `assignVendor` passes 'system' for this check, which is honest: the
         * transition is a consequence of assignment, not an act an admin performs
         * on its own. The Assignment panel is the only door.
         */
        { to: 'ASSIGNED', allowedRoles: ['system'], label: 'Assign to vendor' },
        { to: 'ARTWORK_REJECTED', allowedRoles: ['admin'], label: 'Reject artwork' },
        { to: 'REFUNDED', allowedRoles: ['admin'], label: 'Refund' },
    ],
    ARTWORK_REJECTED: [
        { to: 'ARTWORK_REVIEW', allowedRoles: ['customer', 'admin'], label: 'Re-upload artwork' },
        { to: 'CANCELLED', allowedRoles: ['admin'], label: 'Cancel' },
        { to: 'REFUNDED', allowedRoles: ['admin'], label: 'Refund' },
    ],
    /*
     * Admin sits alongside vendor on every production step.
     *
     * Not a loosening for its own sake: when the shop produces the work itself
     * there is no vendor account to press these buttons, and roles are
     * single-valued so one person cannot be both. Without admin here an in-house
     * order reaches ASSIGNED and stops for good — which is precisely how the
     * first real paid order came to sit untouched.
     */
    ASSIGNED: [
        { to: 'VENDOR_ACCEPTED', allowedRoles: ['vendor', 'admin'], label: 'Accept job' },
        // A decline returns the order to review so admin can pick another vendor.
        { to: 'ARTWORK_REVIEW', allowedRoles: ['vendor', 'admin'], label: 'Decline / reassign' },
        { to: 'CANCELLED', allowedRoles: ['admin'], label: 'Cancel' },
    ],
    VENDOR_ACCEPTED: [
        { to: 'IN_PRODUCTION', allowedRoles: ['vendor', 'admin'], label: 'Start production' },
        { to: 'ARTWORK_REVIEW', allowedRoles: ['admin'], label: 'Reassign' },
        { to: 'CANCELLED', allowedRoles: ['admin'], label: 'Cancel' },
    ],
    IN_PRODUCTION: [
        { to: 'READY_TO_DISPATCH', allowedRoles: ['vendor', 'admin'], label: 'Mark ready' },
        { to: 'CANCELLED', allowedRoles: ['admin'], label: 'Cancel' },
    ],
    READY_TO_DISPATCH: [{ to: 'SHIPPED', allowedRoles: ['vendor', 'admin'], label: 'Dispatch' }],
    SHIPPED: [
        { to: 'DELIVERED', allowedRoles: ['vendor', 'admin', 'system'], label: 'Mark delivered' },
    ],
    DELIVERED: [{ to: 'REFUNDED', allowedRoles: ['admin'], label: 'Refund' }],
    CANCELLED: [{ to: 'REFUNDED', allowedRoles: ['admin'], label: 'Refund' }],
    REFUNDED: [],
};
export function allowedTransitions(from, role) {
    return [...(ORDER_TRANSITIONS[from] ?? [])].filter((t) => t.allowedRoles.includes(role));
}
export function canTransition(from, to, role) {
    return allowedTransitions(from, role).some((t) => t.to === to);
}
/** Statuses that no longer count as open work on the orders board. */
export const TERMINAL_STATUSES = ['DELIVERED', 'CANCELLED', 'REFUNDED'];
export function isTerminal(status) {
    return TERMINAL_STATUSES.includes(status);
}
/** Ordered stages for the customer-facing tracking stepper. */
export const CUSTOMER_TRACKING_STAGES = [
    { key: 'placed', label: 'Order placed', matches: ['PENDING_PAYMENT', 'PAID'] },
    { key: 'artwork', label: 'Artwork check', matches: ['ARTWORK_REVIEW', 'ARTWORK_REJECTED'] },
    { key: 'production', label: 'In production', matches: ['ASSIGNED', 'VENDOR_ACCEPTED', 'IN_PRODUCTION'] },
    { key: 'dispatch', label: 'Dispatched', matches: ['READY_TO_DISPATCH', 'SHIPPED'] },
    { key: 'delivered', label: 'Delivered', matches: ['DELIVERED'] },
];
export function trackingStageIndex(status) {
    return CUSTOMER_TRACKING_STAGES.findIndex((s) => s.matches.includes(status));
}
/* ------------------------------------------------------------------ *
 * Vendor job status — the vendor's private view of one line item
 * ------------------------------------------------------------------ */
export const VENDOR_JOB_TRANSITIONS = {
    ASSIGNED: ['ACCEPTED', 'DECLINED'],
    ACCEPTED: ['IN_PRODUCTION'],
    DECLINED: [],
    IN_PRODUCTION: ['READY'],
    READY: ['DISPATCHED'],
    DISPATCHED: [],
};
export function canVendorTransition(from, to) {
    return (VENDOR_JOB_TRANSITIONS[from] ?? []).includes(to);
}
/**
 * How far along each job status is. An order made of several jobs is only as
 * far along as its *least* advanced one — three line items at three presses
 * have not shipped until all three have gone.
 */
const JOB_PROGRESS = {
    ASSIGNED: 0,
    ACCEPTED: 1,
    IN_PRODUCTION: 2,
    READY: 3,
    DISPATCHED: 4,
    // Declined jobs are excluded before this is consulted; they are awaiting
    // reassignment, not holding the order at a stage.
    DECLINED: -1,
};
const PROGRESS_TO_ORDER_STATUS = {
    0: null, // not every job accepted yet — the order stays where it is
    1: 'VENDOR_ACCEPTED',
    2: 'IN_PRODUCTION',
    3: 'READY_TO_DISPATCH',
    4: 'SHIPPED',
};
/**
 * Order status implied by the jobs beneath it.
 *
 * Taking the minimum rather than matching uniform states matters for mixed
 * orders: one job dispatched and another still ready is READY_TO_DISPATCH,
 * not "no opinion". An earlier version returned null there, which left the
 * order understating its own progress on the tracking page.
 */
export function orderStatusForVendorJobs(jobStatuses) {
    const active = jobStatuses.filter((s) => s !== 'DECLINED');
    if (active.length === 0)
        return null;
    const least = Math.min(...active.map((s) => JOB_PROGRESS[s]));
    return PROGRESS_TO_ORDER_STATUS[least] ?? null;
}
//# sourceMappingURL=orderStatus.js.map